<template>
  <div class="table-list-page">
    <!-- 검색 툴 -->
    <search-tools v-if="showNav" :boardId="boardId" :layout="searchLayout" v-model="nav.value" @search="onClickSearch">
      <slot name="search"></slot>
    </search-tools>

    <div>
      <div class="w-full flex justify-end gap-4 text-right px-4 xl:px-0">
        <!-- 선택 후 xxx 팝업용-->
        <div class="w-full flex" v-if="selectList">
          <vs-tabs v-model="selectTab" class="mr-auto">
            <vs-tab label="검색 결과" />
            <vs-tab :label="`선택된 문서 (${selectList.length})`" />
          </vs-tabs>
          <div class="ml-auto flex" style="gap: 0.5rem">
            <vs-button v-if="selectTab === 0" color="secondary" style="min-width: 180px" @click="onSelectAll">전체선택</vs-button>
            <vs-button :color="selectList.length > 0 ? 'secondary' : 'gray'" style="min-width: 180px" @click="onClickPrint">선택된 문서 인쇄</vs-button>
          </div>
        </div>
        <!-- 목록-->
        <div v-else class="w-full">
          <div class="w-full flex">
            <!-- total -->
            <div v-if="totalCountVisible" class="pl-2 xl:pl-0 mb-4 flex items-center" style="font-size: 18px">
              <slot name="totalCount">
                <span class="mr-2" style="font-weight: 700">{{ totalCountLabel }}</span>
                <span>{{ total }}{{ totalCountUnit }}</span>
              </slot>
            </div>
            <div class="flex ml-auto gap-4">
              <vs-button v-if="useInfoPopup" class="mb-4 flex-1 xl:flex-none; px-3" color="secondary" type="border" @click="onClickInfoPopup">
                <div class="flex items-center justify-center gap-2">
                  <feather-icon icon="InfoIcon" svgClasses="h-5 w-5" />
                  <p>{{ useInfoPopup.title }}</p>
                </div>
              </vs-button>
              <slot name="toolRight"></slot>
              <vs-button v-if="useSelectDelete" class="mb-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickSelectDeleteMode">{{ getSelectDeleteLabel }}</vs-button>
              <vs-button v-if="isSelectDeleteMode" :disabled="editModeSelectList.length === 0" class="mb-4 flex-1 xl:flex-none" color="secondary" @click="onClickSelectDelete">{{ `선택삭제 (${this.editModeSelectList.length})` }}</vs-button>
              <vs-button v-if="useGhsLabelPrint && !isSelectDeleteMode" :disabled="ghsPrintSelectList.length === 0" class="mb-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickGhsLabelPrint">경고표지 출력</vs-button>
              <!-- 1030 전용. 그림문자 이미지 매칭 추가 전에 OCR 로 등록돼 그림문자가 비어 있는 문서를 첨부 PDF 로 다시 읽어 일괄 채운다 -->
              <vs-button v-if="ghsPictogramRescanVisible && !isSelectDeleteMode" class="mb-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickGhsPictogramRescan">그림문자 일괄 재추출</vs-button>
              <vs-button v-if="useExcelDownloadList && !isSelectDeleteMode" class="mb-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickExcelDownload">List Excel 다운로드</vs-button>
              <!-- 위험성평가 상세 Raw data 엑셀 (시스템관리자 전용, src/util/RiskRawExcel.js 참고) -->
              <vs-button v-if="riskRawExcelVisible && !isSelectDeleteMode" class="mb-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickRiskRawExcel">Raw Excel 다운로드</vs-button>
              <vs-button v-if="useExcelDownloadPosts && !isSelectDeleteMode" class="mb-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickSelectDownload"> 선택 후 엑셀다운로드</vs-button>
              <vs-button v-if="multiPrint && !isSelectDeleteMode" class="mb-4 py-0 pl-6 hidden xl:block" color="secondary" type="border" @click="onClickMultiPrint">
                <div class="flex items-center">
                  <feather-icon icon="PrinterIcon" svgClasses="h-6 w-6" class="mr-1" />
                  <span class="my-3">선택 후 인쇄</span>
                </div>
              </vs-button>
              <vs-button v-if="approval && permission.c && !isSelectDeleteMode" color="secondary" type="border" @click="onClickApprovalLine" class="mb-4 xl:flex-none">결재선 지정 </vs-button>
              <vs-button v-if="create && permission.c && !isSelectDeleteMode" color="secondary" @click="onClickNew" class="mb-4 xl:flex-none">{{ createBtnLabel }} </vs-button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="componentMobileCard && isMobile">
        <component :is="componentMobileCard" :parsedListForTable="parsedListForTable" @selected="onClickDetail" class="block xl:hidden">
          <template #empty>
            <div v-if="parsedListForTable.length === 0" class="empty-card">
              <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <div class="empty-title">{{ download }} 데이터가 없습니다</div>
              <div class="empty-description">새로운 {{ download }}을(를) 등록해보세요.</div>
            </div>
          </template>
          <template #pagenation>
            <!--페이지네이션-->
            <div class="vs-con-table" v-if="selectTab === 0">
              <div class="con-pagination-table vs-table--pagination">
                <k-pagination :total="lastPage" :max-items="5" v-model="page" @input="onChangePage"></k-pagination>
              </div>
            </div>
          </template>
        </component>
      </div>

      <component
        v-else
        :is="isListTheme"
        :cardClass="cardClass"
        :baseColumn="baseColumn"
        :heads="heads"
        :parsedListForTable="parsedListForTable"
        :approval="approval"
        :useFinishLater="useFinishLater"
        :boardSlug="boardSlug"
        :moveProject="moveProject"
        :selectList="selectList"
        :multiSelect="isSelectDeleteMode || useGhsLabelPrint"
        :multiSelectList="isSelectDeleteMode ? editModeSelectList : ghsPrintSelectList"
        :fontSize="fontSize"
        :selectTab="selectTab"
        :lastPage="lastPage"
        :page="page"
        :perPage="perPage"
        :loading="loading"
        :class="listTheme"
        :minWidth="minWidth"
        @allCheck="onClickAllCheck"
        @toggleRow="onClickGhsToggleRow"
        @sort="onClickSort"
        @selected="onClickDetail"
        @change="onChangePage"
        @changePerPage="onChangePerPage"
        class="xl:block"
      >
        <template #typeObj="{ trData, head, ri, ci }">
          <div v-if="trData && trData.type === 'child'" class="flex items-center justify-center body-child">
            <div v-for="(_cd, _cd_i) in trData.datas" :key="`table_list_child_${ri}_${ci}_${head.name}_${_cd}_${_cd_i}`" :style="head.child[_cd_i].style ? head.child[_cd_i].style : `flex:1`" style="min-height: 43px">
              {{ _cd }}
            </div>
          </div>
          <div v-else-if="trData && trData.type === 'child-table'" class="body-child-table">
            <div v-for="(row, _r_i) in trData.rows" :key="`table_list_child_table_row_${ri}_${ci}_${head.name}_${_r_i}`" class="body-child-table-row" :class="{ 'is-last': _r_i === trData.rows.length - 1 }">
              <div v-for="(cell, _c_i) in row" :key="`table_list_child_table_cell_${ri}_${ci}_${head.name}_${_r_i}_${_c_i}`" :style="head.child[_c_i].style ? head.child[_c_i].style : `flex:1`" class="body-child-table-cell">
                {{ cell }}
              </div>
            </div>
            <div v-if="!trData.rows || trData.rows.length === 0" class="body-child-table-row is-last">{{ trData.empty || "-" }}</div>
          </div>
          <div v-else-if="trData && trData.type === 'file'">
            <button v-for="(file, index) in trData.files" :key="index" @click.stop="() => onClickFileDownload(file.name || file.fileName, file.path)" style="background: none" class="border-0 cursor-pointer">
              <vs-icon icon-pack="feather" icon="icon-file-text" style="color: #7e72f2" />
            </button>
            <span v-if="trData.files && trData.files.length === 0" v-html="trData.empty || '-'"></span>
          </div>
          <div v-else-if="trData && trData.type === 'fileList'" style="text-align: left" @click.stop>
            <a
              v-for="(file, index) in trData.files"
              :key="index"
              @click.stop="() => onClickFileDownload(file.name || file.fileName, file.path)"
              style="background: none; text-decoration: underline"
              class="border-0 cursor-pointer"
              onmouseover="this.style.color= '#7e72f2';"
              onmouseout="this.style.color= '#3c3c3c';"
            >
              {{ file.name || file.fileName }}
            </a>
            <span v-if="trData.files && trData.files.length === 0" v-html="trData.empty || '-'"></span>
          </div>
          <div v-else-if="trData && trData.type === 'colorBox'" class="color-box">
            <div v-if="trData.text" class="flex items-center justify-center">
              <!-- onClick 이 지정된 뱃지는 행 클릭(상세 이동) 대신 지정된 동작을 실행한다 (예: 823 위험성평가 바로가기) -->
              <p
                :style="trData.color ? `background:${trData.color}` : ''"
                :class="[trData.color ? 'text-white' : '', trData.onClick ? 'cursor-pointer underline' : '']"
                :title="trData.title || ''"
                style="font-size: 14px; font-weight: 500; text-align: center; padding: 2px 7px; border-radius: 4px"
                @click="trData.onClick ? onClickColorBox($event, trData) : null"
              >
                {{ trData.text }}
              </p>
            </div>
            <span v-if="trData.files && trData.files.length === 0" v-html="trData.empty || '-'"></span>
          </div>
          <div v-else-if="trData && trData.type === 'docs'" class="docs-file">
            <button v-if="trData.file && trData.file.path" @click.stop="() => onClickFileDownload(trData.file.name || trData.file.fileName, trData.file.path)" style="background: none" class="border-0 cursor-pointer">
              <span class="underline" style="color: #7e72f2">{{ trData.file.fileName }}</span>
            </button>
            <span v-if="trData.file && !trData.file.path" v-html="trData.empty || '-'"> </span>
            <template v-else-if="trData.files">
              <button v-for="(_file, _file_i) in trData.files" :key="`docs_file_${_file_i}`" @click.stop="() => onClickFileDownload(_file.name, _file.path)" style="background: none" class="border-0 cursor-pointer">
                <span class="underline" style="color: #7e72f2">{{ _file.name }}</span>
              </button>
            </template>
            <span v-if="trData.files && trData.files.length === 0" v-html="trData.empty || '-'"></span>
          </div>
          <div v-else-if="trData && trData.type === 'docsIcon'">
            <a v-if="trData.file && trData.file.path" download target="_blank" :href="trData.file.path" @click.stop>
              <img src="@/assets/images/icon/icon-file.svg" alt="close" class="mx-auto" />
            </a>
            <div v-else-if="trData.file && 0 < trData.file.length"><img src="@/assets/images/icon/icon-file.svg" alt="close" class="mx-auto" /></div>
          </div>
          <div v-else-if="trData && trData.type === 'image'">
            <a download target="_blank" :href="trData.url" @click.stop>
              <vs-image style="width: 120px; height: 120px" :src="trData.url" />
            </a>
          </div>
          <div v-else-if="trData && trData.type === 'image-center'">
            <a download target="_blank" :href="trData.url" style="display: flex; align-items: center; justify-content: center" @click.stop>
              <vs-image style="width: 120px; height: 120px" :src="trData.url" />
            </a>
          </div>
          <div v-else-if="trData && trData.type === 'checkbox'">
            <vs-checkbox :value="trData.value" disabled />
          </div>
          <div v-else-if="trData && trData.type === 'pre'">
            <div style="white-space: pre-line; text-align: left">{{ trData.value }}</div>
          </div>
          <div v-else-if="trData && trData.type === 'pre-center'">
            <div style="white-space: pre-line; text-align: center">{{ trData.value }}</div>
          </div>
          <div v-else-if="trData && trData.type === 'member'">
            <div class="w-full" style="text-align: center">{{ getMemberName(trData.memberId) }}</div>
          </div>
          <div v-else-if="trData && trData.type === 'html'">
            <div class="w-full" style="text-align: center" v-html="trData.html"></div>
          </div>
          <div v-else-if="trData && trData.type === 'link'">
            <a v-if="trData.url" :href="trData.url" target="_blank" @click.stop>
              <feather-icon style="color: #7e72f2; width: 20px; height: 20px" icon="ExternalLinkIcon" />
            </a>
          </div>
          <div v-else-if="trData && trData.type === 'post-link'">
            <router-link
              :to="{
                name: trData.name ? trData.name : 'write|update|tab',
                params: {
                  proj_id: trData.projectId,
                  boardId: trData.boardId,
                  pageType: trData.pageType ? trData.pageType : 'write',
                  postId: trData.postId,
                },
              }"
            >
              <feather-icon style="color: #7e72f2; width: 20px; height: 20px" icon="ExternalLinkIcon" />
            </router-link>
          </div>
          <div v-else-if="trData">
            <span :class="trData.type">{{ trData.value }}</span>
            <span v-if="trData.type === 'up' || trData.type === 'down'" style="display: inline-flex; padding-top: 2px; width: 20px; height: 20px; max-height: 20px">
              <vs-image class="w-full" :src="require(`@/assets/images/icon/Icon-arrow_${trData.type}.png`)" />
            </span>
          </div>
        </template>
      </component>

      <popup-approval-process v-if="approval" :show-popup="popup.approval" @closePopup="onClickClose" />
      <popup-excel-download v-if="download" v-model="popup.excel" :list="customExcelList || excelList" :title="title" :download-func="customDownloadFunc" :default-file-name="customFileName" />
      <popup-info v-if="useInfoPopup" v-model="popup.info" :data="useInfoPopup" />
      <popup-g-h-s-label-print v-if="useGhsLabelPrint" v-model="popup.ghsLabelPrint" :items="ghsLabelItems" />
      <popup-g-h-s-pictogram-rescan v-if="ghsPictogramRescanVisible" v-model="popup.ghsPictogramRescan" :project-id="projectId" @saved="onGhsPictogramRescanSaved" />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import PopupInfo from "@/popup/PopupInfo";
import PopupExcelDownload from "@/popup/PopupExcelDownload";
import PopupApprovalProcess from "@/views/approval/PopupApprovalProcess";
// 1030(MSDS 관리) 전용 "경고표지 출력" 팝업. 이 기능을 쓰는 보드가 1030 뿐이라 여기서 바로 import 한다.
import PopupGHSLabelPrint from "@/views/boards/single-page/1030/PopupGHSLabelPrint.vue";
import PopupGHSPictogramRescan from "@/views/boards/single-page/1030/PopupGHSPictogramRescan.vue";
import SearchNav from "@/components/nav/SearchNav";
import KPagination from "@/components/kPagination/kPagination";
import singlePageList from "@/views/boards/single-page/listIndex";
import searchComponent from "@/views/list/components/include";

// Import Theme
import ThemeOrigin from "./TableListPageTheme/ThemeOrigin.vue";
import ThemeTable from "./TableListPageTheme/ThemeTable.vue";
import SearchTools from "@/views/list/components/SearchTools.vue";
import { downloadFile } from "@/util/DataUtil";
import tabItems from "@/views/boards/tab/tab-items";
import * as DataUtil from "@/util/DataUtil";

import listMobileCard from "@/views/boards/single-page/listMobileCardIndex";
// 위험성평가(813/814) 상세 Raw data 엑셀
import { canDownloadRiskRawExcel, downloadRiskRawExcel } from "@/util/RiskRawExcel";

// (클라이언트 페이징) 페이지/건수 이동은 URL(pg/ppg) 변경 → App.vue 의 router-view key 로 컴포넌트가 재마운트되며 처리된다.
// 이때 전체 목록을 매번 다시 받지 않도록 직전 로드 결과를 모듈 단위로 보관한다.
// fromPaging 플래그가 켜진(= onChangePage/onChangePerPage 로 인한) 재마운트에서만 사용하고, 상세 진입 후 복귀·메뉴 진입·검색·정렬은 항상 새로 받는다.
const clientPagingCache = { key: null, data: null, fromPaging: false };

export default {
  name: "TableListPage",
  components: {
    SearchTools,
    KPagination,
    SearchNav,
    PopupApprovalProcess,
    PopupInfo,
    PopupExcelDownload,
    PopupGHSLabelPrint,
    PopupGHSPictogramRescan,

    ThemeOrigin,
    ThemeTable,
  },
  props: {
    detail: Function,
    list: {
      required: false,
      type: Array,
    },
    // list 를 api 를 통해 불러오지 않고, props 의 list 를 사용하는지에 대한 여부
    useList: Boolean,
    baseColumn: {
      type: Boolean,
      default: true,
    },
    bodyStyle: {
      type: String,
      default: "card",
    },
    showNav: {
      type: Boolean,
      default: true,
    },
    download: {
      type: Boolean | String,
      default: true,
    },
    approval: {
      type: Boolean,
      default: undefined,
    },
    create: {
      type: Boolean,
      default: true,
    },
    createBtnLabel: {
      type: String,
      default: "신규작성",
    },
    moveProject: {
      type: Boolean,
      default: false,
    },
    moveDetail: {
      type: Boolean,
      default: true,
    },
    moveList: {
      type: Boolean,
      default: true,
    },
    moveNew: {
      type: Boolean,
      default: true,
    },
    byCurrentLocation: {
      type: Boolean,
      default: false,
    },
    boardSlug: String,
    boardId: Number | String,
    routeC: String,
    routeU: String,

    fontSize: String,
    minWidth: Number,
    nav: Object,
    heads: Array,
    getCol: Function,
    getRowStyle: Function, // tr 의 스타일을 설정하는 함수
    getRowClass: Function, // tr 의 클래스를 설정하는 함수
    addRowClassStyles: Function, // tr 의 클래스를 설정하는 함수
    // 목록을 불러온 이후 실제 데이터에 값을 저장하기 전에 실행되는 함수 ( this.listAll = response.data 하기 전 )
    prefixFunc: Function,
    querySearchMode: {
      type: Boolean,
      default: false,
    },
    useFinishLater: {
      type: Boolean,
      default: false,
    },
    multiPrint: {
      type: Boolean,
      default: true,
    },
    selectList: Array,
    // [편집] 버튼이 활성화되고,
    // [편집] 버튼을 클릭할 경우 목록의 제일 앞에 체크박스가 노출된다.
    // [편집] 버튼을 클릭할 경우 [편집종료] 버튼으로 이름이 변경되고, 하나라도 체크가 되어 있으면 [선택삭제] 로 변경된다.
    useSelectDelete: Boolean,
    moveBoard: Number,
    moveBoardBtnLabel: {
      type: String,
      default: "위험성 결과 결재",
    },
    totalCountVisible: {
      type: Boolean,
      default: false,
    },
    totalCountLabel: {
      type: String,
      default: "표시된 문서 : ",
    },
    totalCountUnit: {
      type: String,
      default: "건",
    },
    useExcelDownloadList: {
      type: Boolean,
      default: false,
    },
    useGhsLabelPrint: {
      type: Boolean,
      default: false,
    },
    useExcelDownloadPosts: {
      type: Boolean,
      default: false,
    },
    listTheme: {
      type: String,
      default: "origin",
    },
    useRevision: {
      type: Boolean,
      default: false,
    },
    filterSearch: {
      type: Boolean,
      default: false,
    },
    // true 이면 검색 조건에 맞는 목록을 서버에서 전부(onlyMeta) 받아온 뒤, 페이징은 클라이언트에서 처리한다.
    // prefixFunc(예: 897 중복 제거)이 전체 데이터에 적용되므로 total / 페이지 수가 실제 표시 건수와 일치한다.
    // tab-items 의 clientPaging 플래그로 켠다. 필요한 컬럼은 List.js 의 metaKeys 로 지정한다.
    clientPaging: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tabItems,
      searchComponent,
      popup: {
        excel: false,
        approval: false,
        info: false,
        ghsLabelPrint: false,
        ghsPictogramRescan: false,
      },
      editModeSelectList: [],
      ghsPrintSelectList: [],
      ghsLabelItems: [],
      page: 1, // 현재 페이지 번호
      perPage: 10, // 페이지당 데이터 개수
      total: 0, // 총 데이터 개수
      lastPage: 1, // 마지막 페이지 번호
      sortKey: "",
      orderTarget: "created_at",
      orderBy: null,
      /** orderByList : 정렬 배열
       [{ target: "created_at", direction: "desc" }]
      */
      orderByList: [],
      listAll: [],
      // 엑셀 다운로드를 위해서 전체 불러온 데이터
      listAllExcel: [],
      tableList: [],
      permission: {
        c: false,
        r: false,
        u: false,
        d: false,
      },
      selectTab: 0,

      // 마지막 api 호출시 사용한 옵션
      apiLastLoadOptions: null,
      // 게시글 목록을 불러오고 있다면, true
      loading: false,
      // 모바일 화면인지 여부
      isMobile: false,

      // (클라이언트 페이징) 전체 로드가 진행 중이면 true. 진행 중에는 page/lastPage 를 재계산하지 않는다.
      clientPagingLoading: false,

      // customExcelDownload 보드용 — popup 미리보기 list 와 다운로드 콜백
      customExcelList: null,
      customDownloadFunc: null,
      // customExcelDownload 보드는 자체 파일명 규칙을 쓰므로 팝업에 기본 파일명으로 전달한다.
      customFileName: "",
    };
  },
  watch: {
    list() {
      this.listAll = this.list;
    },
  },
  computed: {
    // 위험성평가(813 직영 / 814 수급업체) 상세 Raw data 엑셀 버튼 노출 여부.
    riskRawExcelVisible() {
      return canDownloadRiskRawExcel(this.$store) && [813, 814].includes(Number(this.boardId));
    },
    // 그림문자 일괄 재추출은 다른 사람 문서까지 한꺼번에 수정하는 관리 기능이라 시스템관리자(is_superuser)에게만 보인다
    ghsPictogramRescanVisible() {
      if (!this.useGhsLabelPrint) return false;
      const userInfo = (this.$store.state.auth && this.$store.state.auth.userInfo) || {};
      return parseInt(userInfo.is_superuser) > 0;
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    tab() {
      const tabItem = this.tabItems[this.boardId];
      return tabItem;
    },
    cardClass() {
      if (this.bodyStyle === "none") {
        return "none-card";
      }
      return "";
    },
    title() {
      if (typeof this.download === "string") {
        return this.download;
      }
      return "unknown";
    },
    getPage() {
      const query = this.$route.query;
      if (query && query.pg) {
        try {
          return Number(query.pg) > 0 ? Number(query.pg) : 1;
        } catch (e) {}
      }
      return 1;
    },
    getPerPage() {
      const defaultPerPage = 10;
      const query = this.$route.query;
      if (query && query.ppg) {
        try {
          return Number(query.ppg) > 0 ? Number(query.ppg) : defaultPerPage;
        } catch (e) {}
      }
      return defaultPerPage;
    },
    getEditModeSelectList() {
      const editModeSelectList = localStorage.getItem("editModeSelectList");
      if (editModeSelectList) {
        const editModeSelectListObj = JSON.parse(editModeSelectList);
        if (editModeSelectListObj[`${this.boardId}`]) {
          return editModeSelectListObj[`${this.boardId}`];
        }
      }
      return [];
    },
    getSelectDeleteLabel() {
      if (this.isSelectDeleteMode) {
        return "편집종료";
      }
      return "편집";
    },
    editMode() {
      return this.$route.query["mod"];
    },
    isSelectDeleteMode() {
      if (this.useSelectDelete) {
        return this.editMode === "del";
      }
      return false;
    },
    stdSearchComponents() {
      if (singlePageList[`${this.boardId}_list`]) {
        const getComponents = singlePageList[`${this.boardId}_list`].components;
        if (getComponents) {
          const stdSearchComponents = getComponents();
          if (stdSearchComponents && stdSearchComponents.length > 0) {
            for (const component of stdSearchComponents) {
              component.component = searchComponent[component["component"]];
            }
          }
          return stdSearchComponents;
        }
      }
      return [];
    },
    searchLayout() {
      return this.filterSearch ? "filter" : "default";
    },
    /**
     * 테이블 본문에 해당하는 리스트를 작성해서 반환한다
     */
    parsedListForTable() {
      // 선택된 문서를 별도로 볼 수 있는 탭을 선택하면, 테이블의 리스트가 선택된 문서 리스트로 변경된다.
      let rawList = this.selectTab && this.selectTab === 1 ? this.selectList : this.listAll;
      // 클라이언트 페이징: 전체 목록 중 현재 페이지 구간만 표시한다 (No. 번호는 total 기준으로 계산되므로 그대로 맞는다)
      if (this.clientPaging && this.selectTab === 0) {
        const start = (this.page - 1) * this.perPage;
        rawList = rawList.slice(start, start + this.perPage);
      }
      return this.parsedListForTableWithList(rawList);
    },
    excelList() {
      const info = singlePageList[`${this.boardId}_list`];
      if (info && info.getHead) {
        const _head = info.getHead();
        const list = this.parsedListForTableWithList(this.listAllExcel, true).map((_pl) => {
          const _row = { No: _pl.No };
          _head.map((_h) => {
            if (_h.child) {
              const headChild = _h.child.map((c) => c.name);
              const cell = _pl[_h.name] || {};
              headChild.forEach((c, index) => {
                if (Array.isArray(cell.rows)) {
                  // child-table(1030 구성성분처럼 성분별로 여러 줄) 은 하위 열마다 줄바꿈으로 합쳐 한 행에 담는다.
                  // 기존에는 datas 만 가정해 rows 타입에서 TypeError 가 나 Excel 팝업이 열리지 않았다.
                  _row[c] = cell.rows.map((r) => (r && r[index] !== undefined && r[index] !== null ? `${r[index]}` : "")).join("\n");
                } else {
                  _row[c] = Array.isArray(cell.datas) ? cell.datas[index] : "";
                }
              });
            } else {
              _row[_h.name] = _pl[_h.name];
            }
          });

          return _row;
        });

        return list.reverse();
      }

      return [];
    },

    initSortData() {
      const _query_keys = ["_sort", "_order"];
      const query = this.$route.query;
      const query_sort = query[_query_keys[0]];
      const query_order = query[_query_keys[1]];
      const data = {
        sort: query_sort || "",
        order: query_order || "",
      };
      return data;
    },
    isListTheme() {
      switch (this.listTheme) {
        case "origin":
          return "ThemeOrigin";
        case "table":
        case "striped-table":
        case "striped-table-bordered":
          return "ThemeTable";
      }
    },

    useInfoPopup() {
      if (singlePageList[`${this.boardId}_list`] && singlePageList[`${this.boardId}_list`].getInfoHTML) {
        return singlePageList[`${this.boardId}_list`].getInfoHTML();
      }
      return null;
    },

    componentMobileCard() {
      return listMobileCard[`${this.boardId}_listmobilecard`];
    },
  },
  async created() {
    this.page = this.getPage;
    this.perPage = this.getPerPage;
    this.editModeSelectList = this.getEditModeSelectList;
    this.sortKey = this.initSortData.sort;
    this.orderBy = this.initSortData.order;

    // 게시판일 경우, 권한을 확인한다
    if (Number.isInteger(parseInt(this.boardId))) {
      let boards = this.$store.state.member.memberInfo.boards;
      if (!boards) {
        await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
        boards = this.$store.state.member.memberInfo.boards;
      }

      let board = boards.find((b) => parseInt(b.id) === parseInt(this.boardId));
      if (!!board && !!board.pivot) {
        if (board.pivot.project_id) {
          // 프로젝트에 맞는 권한이 있는지 다시 조사한다
          board = boards.find((b) => parseInt(b.pivot.board_id) === parseInt(this.boardId) && parseInt(b.pivot.project_id) === parseInt(this.projectId));
          this.permission = { ...board.pivot };
        } else {
          this.permission = { ...board.pivot };
        }
      }
    }
  },
  mounted() {
    if (!this.useList) {
      this.loadList();
    }

    // 이벤트버스를 통해서 검색어가 변경되었을 때, 리스트를 다시 불러온다
    if (this.$eventbus) {
      this.$eventbus.$on("keyword-search", (/*keyword*/) => {
        this.loadList();
      });
    }

    // 모바일 화면 감지 설정
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    checkMobile() {
      // xl breakpoint (1280px) 미만을 모바일로 간주
      this.isMobile = window.innerWidth < 1280;
    },
    initSort() {
      const { sortKey, orderBy } = this;
      // 정렬 순서가 정해졌을 때에만 해당 컬럼의 정보를 추출한다
      if (orderBy) {
        this.orderDirection = orderBy;
        const target = this.getColumnKey(sortKey);
        if (target) {
          this.orderTarget = target;
        } else {
          // 만약 target이 없다면, 해당 컬럼의 정보를 찾아서 정렬한다
          const target = this.findKeyWithHead(sortKey);
          this.orderTarget = target || "created_at";

          this.orderByList = [{ target: `$${sortKey}`, direction: orderBy }];
        }
      }
      // 정렬이 해제될 때에는 기본 컬럼을 "created_at"으로 설정한다
      else {
        this.orderDirection = "desc";
        this.orderTarget = "created_at";

        const listInfo = singlePageList[`${this.boardId}_list`];
        if (listInfo) {
          this.orderByList = (listInfo.getDefaultSort && listInfo.getDefaultSort()) || [{ target: "$created_at", direction: "desc" }];
        }
      }
    },
    initSearchKeyValue() {
      let keyValue = {};
      let keyword = [];

      if (singlePageList[`${this.boardId}_list`]) {
        const listInfo = singlePageList[`${this.boardId}_list`];
        keyValue = listInfo.getSearchKeyValue ? listInfo.getSearchKeyValue(this.nav) : "";
        keyword = listInfo.getSearchKeyword ? listInfo.getSearchKeyword(this.nav) : "";
      }

      if (keyword) {
        keyword = keyword.map((_keyword) => {
          return _keyword
            .replace("field_id", `nav.page.p${this.$route.params.boardId}.fieldId`)
            .replace("default_skw", `nav.page.p${this.$route.params.boardId}.searchKeyword`)
            .replace("default_disaster", `nav.page.p${this.$route.params.boardId}.disasterType`);
        });
      }

      if (this.nav) {
        if (this.nav.page) this.nav.page[`p${this.boardId}`] = keyValue;
        this.nav.keyword = keyword;
      }
    },
    async initSearchConditions() {
      const func = singlePageList[`${this.boardId}_list`] && singlePageList[`${this.boardId}_list`].getConditions;
      if (func) {
        this.nav.conditions = await func(this.nav, this);
      }
    },

    initListOptions() {
      const func = singlePageList[`${this.boardId}_list`] && singlePageList[`${this.boardId}_list`].getOptions;
      if (func) {
        this.nav.options = func(this.nav);
      }
    },

    getMemberName(memberId) {
      const list = this.$store.state.member.list || [];
      for (const member of list) {
        if (Number(`${member.id}`) === Number(`${memberId}`)) {
          return member.name;
        }
      }

      return "";
    },

    async loadList(forExcelDownload = false) {
      // 결재문서인지 아닌지 확인되지 전까지는 0.3초 후 재시도한다
      // undefined 일 경우, 결재문서인지 아닌지 확인되지 않은 상태
      // true 일 경우, 결재문서
      // false 일 경우, 일반문서
      // ** approval 확인이 느린 이유 : tab/Index.vue 의 initTab 과정을 거쳐 this.tab 에 값을 넣기까지가 오래걸리는 것으로 판단됨.
      if (typeof this.approval === "undefined") {
        setTimeout(() => {
          this.loadList(forExcelDownload);
        }, 300);
        return;
      }

      let projectId = this.$route.params.proj_id;
      if (this.nav && this.nav.projectId) {
        projectId = this.nav.projectId;
      }

      this.initSort();
      this.initSearchKeyValue();
      await this.initSearchConditions();
      this.initListOptions();

      if (this.approval) {
        await this.loadListApproval(projectId, forExcelDownload);
      } else {
        await this.loadListBoard(projectId, forExcelDownload);
      }
    },

    async loadListBoard(projectId, forExcelDownload = false) {
      if (!forExcelDownload) {
        this.loading = true;
      }
      try {
        const boardId = this.boardId;
        const conditions = this.nav && this.nav.conditions ? this.nav.conditions : [];
        // 작성자 meta 기준 검색 조건 (List.js 의 getConditions 에서 nav.authorConditions 로 설정)
        const authorConditions = this.nav && this.nav.authorConditions ? this.nav.authorConditions : [];
        if (!boardId) {
          return;
        }
        let page = this.page;
        let perPage = this.perPage;
        let orderTarget = this.orderTarget;
        let orderDirection = this.orderDirection;
        let orderByList = this.orderByList;
        if (orderByList.length === 0) {
          orderByList.push({
            target: `$${orderTarget}`,
            direction: orderDirection,
          });
        }

        let companies = this.nav && this.nav.value && this.nav.value.companies && this.nav.value.companies.length > 0 ? this.nav.value.companies : [];
        if (`${boardId}` === "433" && this.$store.state.company.companyTree) {
          const navValue = this.nav.value;
          companies = [];
          const depth = this.$store.state.company.companyTree
            .filter((item) => {
              if (navValue.companyTree && navValue.companyTree.path) {
                return item.company_path.indexOf(navValue.companyTree.path) === 0;
              } else if (navValue.company0) {
                if (navValue.company1) {
                  if (navValue.company2) {
                    return item.company_path.indexOf(`${navValue.company0}/${navValue.company1}/${navValue.company2}/`) === 0;
                  } else {
                    return item.company_path.indexOf(`${navValue.company0}/${navValue.company1}/`) === 0;
                  }
                } else {
                  return false; //item.company_path.indexOf(`${navValue.company0}/`) === 0; // companyId가 너무 많아 호출 오류가 발생하여 주석처리
                }
              } else {
                return false;
              }
            })
            .map((item) => {
              return {
                value: item.descendant_id,
                text: item.descendant.com_name,
              };
            });

          for (const dep of depth) {
            companies.push(dep.value);
          }
        }

        // List.js 의 getConditions 에서 nav.companyIds 로 설정한 작성 업체 검색 조건 병합
        if (this.nav && this.nav.companyIds && this.nav.companyIds.length > 0) {
          for (const id of this.nav.companyIds) {
            if (!companies.includes(id)) {
              companies.push(id);
            }
          }
        }
        const companyIds = [...companies];

        let dateTarget = "created_at";
        let dateBegin = "";
        let dateEnd = "";
        const keywords = [];

        if (this.nav && this.nav.dateTarget) {
          dateTarget = this.nav.dateTarget;
        }

        if (this.nav && this.nav.dateBegin) {
          dateBegin = this.getValue(this.$props, this.nav.dateBegin);
        }

        if (this.nav && this.nav.dateEnd) {
          dateEnd = this.getValue(this.$props, this.nav.dateEnd);
        }

        if (this.nav && this.nav.keyword && this.nav.keyword.length > 0) {
          try {
            this.nav.keyword.map((word) => {
              if (word.substr(0, 14) === "customKeyword_") {
                keywords.push(word.substr(14));
              } else {
                const methods = `${word}`.split("*");
                const key = methods[1].replace(/%*\[\:([^%]*)\]%*/, "$1");
                const value = this.getValue(this.$props, key);
                const query = methods[1].replace(/(%*)([^%]*)(%*)/, `$1${value}$3`);
                const keyword = `${methods[0]}*${query.trim()}`;

                //const key
                if (value) {
                  keywords.push(keyword);
                }
              }
            });
          } catch (e) {}
        }

        // 추후보완 관련
        if (this.nav.value.isFinishLater) {
          keywords.push("content$body.isFinishLater*true");
        }

        const options = {
          projectId: projectId > 0 ? projectId : undefined,
          boardId,
          page,
          perPage,
          orders: orderByList,
          companyIds,

          dateTarget,
          dateBegin,
          dateEnd,

          conditions,
          authorConditions,
          keyword: keywords,
        };

        // 클라이언트 페이징: 페이지/건수는 고정하고(전체 로드), List.js 의 metaKeys 가 있으면 필요한 컬럼만 받아온다
        if (this.clientPaging) {
          options.page = 1;
          options.perPage = 200;
          options.clientPaging = true;
          const listInfo = singlePageList[`${this.boardId}_list`];
          if (listInfo && Array.isArray(listInfo.metaKeys) && listInfo.metaKeys.length > 0) {
            options.withMeta = true;
            options.onlyMeta = true;
            options.withoutRelation = true;
            options.metaKeys = listInfo.metaKeys;
          }
        }

        if (this.clientPaging && forExcelDownload) {
          // 전체 목록을 이미 들고 있으므로 그대로 엑셀에 사용한다
          this.listAllExcel = [...this.listAll];
          return;
        }

        if (!forExcelDownload) {
          if (JSON.stringify(options) === JSON.stringify(this.apiLastLoadOptions)) {
            // 이전과 동일한 옵션으로 리스트를 불러옵니다.
            this.loading = false;
            // 클라이언트 페이징은 데이터가 그대로여도 perPage 변경에 맞춰 페이지 수를 다시 계산한다.
            // 단, 최초 전체 로드가 아직 진행 중일 때(k-pagination 마운트 시 emit 되는 change 로 재진입하는 경우)는
            // listAll 이 비어 있어 page 가 1로 잘못 보정되므로 건너뛴다.
            if (this.clientPaging && !this.clientPagingLoading) this.applyClientPaging();
            return;
          }
          this.apiLastLoadOptions = JSON.parse(JSON.stringify(options));
        }

        if (forExcelDownload) {
          options.page = 1;
          options.perPage = 100;
          options.orderByList = [{ target: "id", direction: "asc" }];

          this.listAllExcel = await this.applyPrefixFuncForExcel(await DataUtil.loadDefragmentation(this.$store, "board/LOAD_POST_LIST", options), options);
        } else if (this.clientPaging) {
          const cacheKey = JSON.stringify(options);
          const usePagingCache = clientPagingCache.fromPaging && clientPagingCache.key === cacheKey && Array.isArray(clientPagingCache.data);
          clientPagingCache.fromPaging = false;

          if (usePagingCache) {
            // 페이지 이동으로 인한 재마운트: 직전에 받아둔 전체 목록(prefixFunc 적용 완료)을 그대로 사용한다
            this.listAll = clientPagingCache.data;
          } else {
            // 전체를 다 받을 때까지 로딩창을 띄워 조작을 막는다 (캐시 재사용 시에는 띄우지 않음)
            this.clientPagingLoading = true;
            this.loadingDialog(true);
            try {
              let response = await this.loadAllPages(options);

              // 목록 데이터를 저장하기 전 작업 (전체 목록에 대해 1회 적용)
              if (this.prefixFunc) {
                response = await this.prefixFunc(this, response, options);
              }

              this.listAll = response.data;
              clientPagingCache.key = cacheKey;
              clientPagingCache.data = this.listAll;
            } finally {
              this.clientPagingLoading = false;
              this.loadingDialog(false);
            }
          }
          this.applyClientPaging();

          this.$emit("load", this.listAll);
        } else {
          let response = await this.$store.dispatch("board/LOAD_POST_LIST", options);

          // 목록 데이터를 저장하기 전 작업
          if (this.prefixFunc) {
            response = await this.prefixFunc(this, response, options);
          }

          this.listAll = response.data;
          this.total = response.total;
          this.lastPage = response.last_page;
          this.page = this.page > this.lastPage ? this.lastPage : this.page;

          this.$emit("load", this.listAll);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async loadListApproval(projectId, forExcelDownload = false) {
      this.loading = true;

      try {
        const boardSlug = this.boardSlug;
        const conditions = this.nav && this.nav.conditions ? this.nav.conditions : [];
        let page = this.page;
        let perPage = this.perPage;
        let orderTarget = this.orderTarget;
        let orderDirection = this.orderDirection;
        const dateTarget = [];
        const dateBegin = [];
        const dateEnd = [];
        const keywords = [];
        const companies = this.nav.value && this.nav.value.companies && this.nav.value.companies.length > 0 ? this.nav.value.companies : [];
        const companyIds = [...companies];

        if (this.nav.keyword && this.nav.keyword.length > 0) {
          try {
            this.nav.keyword.map((word) => {
              const methods = `${word}`.split("*");
              const key = methods[1].replace(/%*\[\:([^%]*)\]%*/, "$1");
              const value = this.getValue(this.$props, key);
              const query = methods[1].replace(/(%*)([^%]*)(%*)/, `$1${value}$3`);
              const keyword = `${methods[0]}*${query.trim()}`;
              //const key
              if (value) {
                keywords.push(keyword);
              }
            });
          } catch (e) {}
        }

        if (this.nav.dateTarget) {
          if (typeof this.nav.dateTarget === "string") {
            dateTarget.push(this.nav.dateTarget);
          } else {
            dateTarget.push(...this.nav.dateTarget);
          }
        }

        if (this.nav.dateBegin && this.nav.dateBegin.length > 0) {
          if (typeof this.nav.dateBegin === "string") {
            dateBegin.push(this.getValue(this.$props, this.nav.dateBegin));
          } else {
            this.nav.dateBegin.map((key) => {
              dateBegin.push(this.getValue(this.$props, key));
            });
          }
        }

        if (this.nav.dateEnd && this.nav.dateEnd.length > 0) {
          if (typeof this.nav.dateEnd === "string") {
            dateEnd.push(this.getValue(this.$props, this.nav.dateEnd));
          } else {
            this.nav.dateEnd.map((key) => {
              dateEnd.push(this.getValue(this.$props, key));
            });
          }
        }

        const options = {
          boardSlug,
          projectId,
          page,
          perPage,
          dateTarget,
          dateBegin,
          dateEnd,
          keyword: keywords,
          orderTarget,
          orderDirection,
          companyIds,
          conditions,
        };

        // 결재 상태를 제한할 경우 설정하여 전달
        if (this.nav.options && this.nav.options.statusIds) {
          options.statusIds = this.nav.options.statusIds;
        }

        // 개정문서 관련
        // options.isParent ( true : 과거문서, false : 최신문서 )
        if (this.tab.useRevision) {
          options.isParent = this.$route.query.isParent === "true" ? true : false;
        }

        if (!forExcelDownload) {
          if (JSON.stringify(options) === JSON.stringify(this.apiLastLoadOptions)) {
            // 이전과 동일한 옵션으로 리스트를 불러옵니다.
            return;
          }
          this.apiLastLoadOptions = JSON.parse(JSON.stringify(options));
        }

        /**
         * 기본 결재문서 목록은 "진행 중" 또는 "완료" 상태의 문서를 불러온다.
         */
        let apiType = "approval/LOAD_POST_PENDING_OR_APPROVED";
        if (this.nav.options && this.nav.options.type && this.nav.options.type === "all") {
          apiType = "approval/LOAD_POST_ALL";
        }

        if (forExcelDownload) {
          options.page = 1;
          options.perPage = 100;
          options.orderTarget = "id";
          options.orderDirection = "desc";
          this.listAllExcel = await this.applyPrefixFuncForExcel(await DataUtil.loadDefragmentation(this.$store, apiType, options), options);
        } else {
          let response = await this.$store.dispatch(apiType, options);

          // 목록 데이터를 저장하기 전 작업
          if (this.prefixFunc) {
            response = await this.prefixFunc(this, response, options);
          }
          this.listAll = response.data;
          this.total = response.total;
          this.lastPage = response.last_page;
          // this.listAll = [...JSON.parse(JSON.stringify(this.$store.state.approval.list))];
          // this.total = this.$store.state.approval.postListInfo.total;
          this.$emit("load", this.listAll);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    // (클라이언트 페이징) 첫 페이지로 전체 페이지 수를 확인한 뒤 나머지 페이지를 병렬로 받아 하나의 응답 모양({ data })으로 합친다.
    // onlyMeta 응답은 post_content 가 없고 postContents(객체)만 있으므로, List.js 의 getCol/getRowStyle 등이
    // JSON.parse(post_content) 로 읽을 수 있도록 post_content 를 채워 넣는다.
    async loadAllPages(options) {
      const first = await this.$store.dispatch("board/LOAD_POST_LIST", { ...options, page: 1 });
      let data = [...(first.data || [])];
      const lastPage = Number(first.last_page) || 1;
      if (lastPage > 1) {
        const promises = [];
        for (let p = 2; p <= lastPage; p++) {
          promises.push(this.$store.dispatch("board/LOAD_POST_LIST", { ...options, page: p }));
        }
        const responses = await Promise.all(promises);
        for (const res of responses) {
          data = data.concat((res && res.data) || []);
        }
      }
      for (const post of data) {
        if (!post.post_content && post.postContents && Object.keys(post.postContents).length > 0) {
          post.post_content = JSON.stringify(post.postContents);
        }
      }
      return { ...first, data };
    },

    // (클라이언트 페이징) 전체 목록 기준으로 total / lastPage / page 를 다시 계산한다
    applyClientPaging() {
      const total = this.listAll.length;
      this.total = total;
      this.lastPage = Math.max(1, Math.ceil(total / this.perPage));
      // 데이터가 없을 때는 URL 의 pg 를 보존한다 (로드 전 상태에서 page 가 1로 덮어써지는 것을 방지)
      if (total > 0 && this.page > this.lastPage) this.page = this.lastPage;
      if (this.page < 1) this.page = 1;
    },

    // 엑셀 다운로드는 loadDefragmentation 으로 배열을 바로 받아서 목록 조회 경로와 달리 prefixFunc 을 거치지 않는다.
    // 그러면 prefixFunc 으로 보강한 값(예: 813/814의 공정분류표 최신값)이 엑셀에만 빠져서
    // 화면과 다운로드 결과가 서로 달라진다. 목록 응답 모양({ data })으로 감싸서 동일하게 처리한다.
    async applyPrefixFuncForExcel(rawList, options) {
      if (!this.prefixFunc) return rawList;

      try {
        const response = await this.prefixFunc(this, { data: rawList || [] }, options);
        return (response && response.data) || rawList;
      } catch (e) {
        console.error(e);
        return rawList;
      }
    },

    parsedListForTableWithList(rawList, forExcel = false) {
      const tableList = [];
      // this.total 이 문자열("141")로 내려오는 환경에서 idx += -1 이 문자열 연결("141-1")로
      // 동작해 두 번째 행부터 No. 값이 NaN 이 되는 문제가 있어, 명시적으로 숫자로 변환한다.
      const totalCount = Number(this.total);
      let idx = this.selectTab === 0 ? (isNaN(totalCount) ? 0 : totalCount) : 1;
      for (const item of rawList) {
        const row = {};
        const content = item.post_content ? JSON.parse(item.post_content).body : {};
        row.projId = item.project_id;
        row.postId = item.post_id || item.id;
        // if (this.boardSlug) {
        //   row["No"] = item.post_id;
        // } else {
        //   row["No"] = item.id;
        // }
        for (let ci = 0; ci < this.heads.length; ci++) {
          if (typeof this.getCol(ci, item) === "object") {
            if (forExcel) {
              if (this.getCol(ci, item).value) {
                row[this.heads[ci].name] = this.getCol(ci, item).value;
              } else {
                row[this.heads[ci].name] = this.getCol(ci, item);
              }
            } else {
              row[this.heads[ci].name] = this.getCol(ci, item);
            }
          } else {
            row[this.heads[ci].name] = this.getCol(ci, item);
          }
        }
        if (this.boardSlug) {
          if (item.document_status) {
            row["결재상태"] = item.document_status.name;
          } else {
            row["결재상태"] = "-";
          }
        }
        if (content) {
          row["문서상태"] = content.isFinishLater ? "추후보완" : "완료";
        }
        row["raw"] = item;

        if (this.selectTab === 0) {
          row["No"] = idx - (this.page - 1) * this.perPage;
        } else {
          // 선택된 문서(팝업용)
          row["No"] = idx;
        }
        idx += this.selectTab === 0 ? -1 : 1;

        if (this.getRowStyle) {
          const newStyle = this.getRowStyle(item);
          row.style = row.style ? `${row.style}; ${newStyle}` : newStyle;
        }

        if (this.getRowClass) {
          const newClass = this.getRowClass(item);
          row.class = row.class ? `${row.class} ${newClass}` : newClass;
        }

        tableList.push(row);
      }

      // 컴포넌트에서 추가한 클래스를 추가
      if (this.addRowClassStyles) {
        this.addRowClassStyles();
      }

      return tableList;
    },

    getValue(obj, keyPath) {
      const keys = `${keyPath}`.split(".");
      let value = obj;
      for (const key of keys) {
        value = value[key] ? value[key] : "";
      }
      return value;
    },

    onClickSearch() {
      const listInfo = singlePageList[`${this.boardId}_list`];
      if (listInfo && listInfo.validate) {
        const msg = listInfo.validate(this.nav);
        if (msg) {
          window.dispatchEvent(
            new CustomEvent("customAlert", {
              detail: { title: "검색 오류", message: msg, useConfirm: false, confirmBtn: "확인" },
            })
          );
          return;
        }
      }

      this.page = 1;
      this.selectTab = 0;

      if (this.querySearchMode) {
        this.onSearchMovePage();
      } else {
        this.loadList();
      }
    },
    // 정렬해야 하는 데이터 컬럼 정보를 불러온다
    getColumnKey(column) {
      const params = { value: { searchType: column } };
      const listInfo = singlePageList[`${this.boardId}_list`];
      const target = listInfo.getSearchKeyword ? listInfo.getSearchKeyword(params) : undefined;

      // 만약 target이 배열이고, 개수가 1개 이상이어야 다음으로 진행할 수 있다
      if (Array.isArray(target) && target.length > 0) {
        // target을 * 문자로 잘라서, 첫번째 내용을 this.orderTarget에 입력한다
        return target[0].split("*")[0];
      }

      return null;
    },
    onClickSort(sortKey, orderBy) {
      this.sortKey = sortKey;
      this.orderBy = orderBy;

      if (sortKey === null) {
        return;
      }
      this.loadList();
    },
    async onChangePage(page) {
      // 클라이언트 페이징: 이어지는 재마운트에서 전체 목록을 다시 받지 않도록 표시한다.
      // k-pagination 은 마운트 시점과 page 값이 보정될 때에도 현재 값 그대로 change 를 emit 하므로,
      // 실제로 페이지가 바뀌는 경우(사용자 클릭)에만 켠다. 그 외 진입은 항상 새로 받아야 한다.
      if (this.clientPaging && Number(page) !== Number(this.page)) clientPagingCache.fromPaging = true;
      this.page = page;
      if (this.querySearchMode) {
        this.onSearchMovePage();
      } else {
        await this.loadList();
      }
    },
    onChangePerPage(perPage) {
      // vs-select 는 리렌더 시 같은 값을 그대로 다시 emit 한다. 그때까지 1페이지로 되돌리면
      // 사용자가 누른 페이지 이동과 경합해 router.push 가 중복되고(Navigation cancelled)
      // 타이밍에 따라 1페이지로 튕긴다. 건수가 실제로 바뀐 경우에만 처리한다.
      if (Number(perPage) === Number(this.perPage)) return;

      // 클라이언트 페이징: 건수 변경도 페이지 이동과 같이 캐시를 재사용한다
      if (this.clientPaging) clientPagingCache.fromPaging = true;
      this.perPage = perPage;
      this.onChangePage(1);
    },
    onClickInfoPopup() {
      this.popup.info = true;
    },
    onClickSelectDeleteMode() {
      if (this.isSelectDeleteMode) {
        this.editModeSelectList = [];
        localStorage.removeItem("editModeSelectList");

        const query = JSON.parse(JSON.stringify(this.$route.query));
        delete query.mod;
        this.$router.replace({ query });
      } else {
        this.$router.replace({ query: { ...this.$route.query, mod: "del" } });
      }
    },
    onClickSelectDelete() {
      // 선택한 게시글을 삭제하시겠습니까?
      const cancelFunction = async () => {
        this.loadingDialog(true);

        const errPostList = []; // 삭제 실패한 문서
        for (const item of this.editModeSelectList) {
          try {
            await this.$store.dispatch("board/DELETE_POST", { boardId: this.boardId, postId: item.postId });
          } catch (err) {
            errPostList.push({
              post: item,
              err,
            });
          }
        }

        this.loadingDialog(false);

        // 삭제 후 목록을 다시 불러오기 위해서 load 에 사용한 options 를 초기화한다
        this.apiLastLoadOptions = {};
        await this.loadList();
      };

      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: "게시글 삭제",
          message: "<div style='width:100%; text-align: center; padding: 8px 0;'>" + "<p>게시글을 삭제하시겠습니까?</p>" + `<p>선택된 문서: ${this.editModeSelectList.length}건</p>` + "</div>",
          useConfirm: true,
          confirmBtn: "취소",
          cancelBtn: "삭제",
          cancelFunction,
          size: "small",
          btnPosition: "center",
          confirmBtnStyle: {
            type: "filled",
            color: "gray",
          },
          cancelBtnStyle: {
            type: "filled",
            color: "danger",
          },
        },
      });
      window.dispatchEvent(customAlert);
    },
    /**
     * 위험성평가 상세 Raw data 엑셀 다운로드 (시스템관리자 전용)
     *
     * loadList(true) 는 화면과 동일한 검색 조건으로 모든 페이지를 100건씩 받아 this.listAllExcel 에 채우고,
     * prefixFunc(813 은 736, 814 는 895 최신 분류 매핑)까지 적용해 준다.
     * 그 결과를 위험성평가표 행 단위로 펼쳐 저장한다.
     */
    async onClickRiskRawExcel() {
      await this.loadList(true);

      this.loading = true;
      try {
        const count = await downloadRiskRawExcel(this.listAllExcel, `${this.download}`, Number(this.boardId));
        console.log(`[RawExcel] 문서 ${this.listAllExcel.length}건 -> ${count}행`);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async onClickExcelDownload() {
      // 전체 문서를 100건씩 모두 받아오는 동안 화면에 아무 반응이 없어 멈춘 것처럼 보이므로,
      // 팝업이 열리기 전까지 목록 영역에 로딩 스피너를 표시한다. (loadList 의 excel 경로는 loading 을 직접 켜지 않는다)
      this.loading = true;
      try {
        await this.loadList(true);
      } catch (e) {
        console.error(e);
        return;
      } finally {
        this.loading = false;
      }

      const info = singlePageList[`${this.boardId}_list`];
      const yearSuffix = this.nav.value.year || this.$moment().format("YYYYMMDD_HHmmss");
      const filename = `${this.download}_${yearSuffix}`;

      // 미리보기용 데이터 — 보드가 getExcelList 를 제공하면 사용, 아니면 기본 excelList 사용
      if (info && info.getExcelList) {
        this.customExcelList = info.getExcelList(this.parsedListForTable, this.listAllExcel);
      } else {
        this.customExcelList = null;
      }

      // 다운로드 버튼 동작 — customExcelDownload 가 있으면 popup 의 downloadFunc 로 위임
      if (info && info.customExcelDownload) {
        const parsed = this.parsedListForTable;
        const list = this.listAllExcel;
        // 팝업에서 사용자가 파일명을 수정한 경우 그 값을 사용한다.
        this.customDownloadFunc = (customFileName) => info.customExcelDownload(parsed, customFileName || filename, list);
        this.customFileName = filename;
      } else {
        this.customDownloadFunc = null;
        this.customFileName = "";
      }

      this.popup.excel = true;
    },
    onClickApprovalLine() {
      this.popup.approval = true;
    },
    onClickNew() {
      clientPagingCache.fromPaging = false;
      if (this.moveNew) {
        const boardId = this.boardId;
        const query = this.$route.query;
        const pageType = "write";
        this.$router.push({
          name: this.routeC,
          params: { boardId, pageType },
          query,
        });
      } else {
        this.$emit("new");
      }
    },
    onClickClose() {
      const keys = Object.keys(this.popup);
      keys.map((key) => {
        this.popup[key] = false;
      });
    },
    onClickAllCheck() {
      if (this.isSelectDeleteMode) {
        if (this.parsedListForTable.every((item) => this.editModeSelectList.find((select) => select.postId === item.postId))) {
          this.editModeSelectList = [];
        } else {
          //parsedListForTable 중 editModeSelectList 에 없는 것만 추가
          this.editModeSelectList.push(...this.parsedListForTable.filter((item) => !this.editModeSelectList.find((select) => select.postId === item.postId)));
        }
        return;
      }
      if (this.useGhsLabelPrint) {
        if (this.parsedListForTable.every((item) => this.ghsPrintSelectList.find((select) => select.postId === item.postId))) {
          this.ghsPrintSelectList = [];
        } else {
          this.ghsPrintSelectList.push(...this.parsedListForTable.filter((item) => !this.ghsPrintSelectList.find((select) => select.postId === item.postId)));
        }
      }
    },
    onClickGhsToggleRow(item) {
      const idx = this.ghsPrintSelectList.findIndex((select) => select.postId === item.postId);
      if (idx >= 0) {
        this.ghsPrintSelectList.splice(idx, 1);
      } else {
        this.ghsPrintSelectList.push(item);
      }
    },
    onClickGhsPictogramRescan() {
      this.popup.ghsPictogramRescan = true;
    },
    onGhsPictogramRescanSaved() {
      // 저장된 그림문자가 목록 선택 → 경고표지 출력에 바로 반영되도록 목록을 다시 불러온다
      this.ghsPrintSelectList = [];
      this.loadList();
    },
    onClickGhsLabelPrint() {
      if (this.ghsPrintSelectList.length === 0) return;
      const listInfo = singlePageList[`${this.boardId}_list`];
      if (!listInfo || !listInfo.getGhsLabelData) {
        this.$vs.notify({ title: "알림", text: "이 게시판은 경고표지 출력을 지원하지 않습니다.", color: "warning" });
        return;
      }
      const func = {
        getContent: (data) => (data.post_content ? JSON.parse(data.post_content).body : {}),
      };
      this.ghsLabelItems = this.ghsPrintSelectList.map((item) => listInfo.getGhsLabelData(item.raw || item, func));
      this.popup.ghsLabelPrint = true;
    },
    /**
     * colorBox 셀에 onClick 이 지정된 경우: 행 클릭(상세 이동)을 막고 지정된 동작만 실행한다
     */
    onClickColorBox(event, trData) {
      event.stopPropagation();
      trData.onClick();
    },
    onClickDetail(value, target) {
      // 클라이언트 페이징: 목록을 벗어나면 페이지 이동용 캐시 플래그를 해제해, 복귀 시 항상 최신 목록을 다시 받도록 한다
      clientPagingCache.fromPaging = false;
      if (this.isSelectDeleteMode) {
        // 선택삭제 모드에서는 선택된 목록을 추가하거나 제거한다
        const selectPost = this.editModeSelectList.find((item) => item.postId === value.postId);
        if (selectPost) {
          this.editModeSelectList = this.editModeSelectList.filter((item) => item.postId !== value.postId);
        } else {
          this.editModeSelectList.push(value);
        }
        return;
      }

      if (!value.postId) return;

      let proj_id = this.projectId;
      if (this.byCurrentLocation) proj_id = this.projectId;

      const boardId = this.boardId;
      const boardSlug = this.boardSlug;
      const postId = value.postId;
      const query = this.$route.query;
      const pageType = "write";
      const route = {
        name: this.routeU,
        params: {
          proj_id,
          boardId,
          boardSlug,
          postId,
          pageType,
        },
        query,
      };

      if (this.moveProject) {
        if (target === "self") {
          if (route.params.boardId === 50) {
            this.$router.push({ name: route.name, params: { proj_id, postId, tab: "info" } });
          } else {
            this.$router.push(route);
          }
          return;
        } else if (target === "_blank") {
          if (route.params.boardId === 50) {
            window.open(`/project/${proj_id}/plan/partners/info/${postId}`, "_blank");
          } else {
            const routeData = this.$router.resolve(route);
            window.open(routeData.href, "_blank");
          }
          return;
        }
      } else if (this.moveDetail) {
        this.$router.push(route);
      }

      this.$emit("detail", value, target, route);
    },

    onSearchMovePage() {
      // url query 문 생성
      let search = JSON.parse(JSON.stringify(this.nav.value));
      const query = JSON.parse(JSON.stringify(this.$route.query));
      query["pg"] = this.page;
      query["ppg"] = this.perPage;

      // (선택삭제 모드) 선택한 목록을 로컬스토리지에 저장
      if (this.isSelectDeleteMode) {
        let editModeSelectList = localStorage.getItem("editModeSelectList");
        if (!editModeSelectList) {
          editModeSelectList = {
            [`${this.boardId}`]: [],
          };
        } else {
          editModeSelectList = JSON.parse(editModeSelectList);
        }
        editModeSelectList[`${this.boardId}`] = this.editModeSelectList;
        localStorage.setItem("editModeSelectList", JSON.stringify(editModeSelectList));
      }

      const searchKeys = Object.keys(search);
      if (searchKeys.length > 0) {
        for (const key of searchKeys) {
          if (key === "searchKeyword") {
            if (search.searchKeyword) {
              query["keyword"] = search.searchKeyword.trim();
            }
          } else if (key === "companies") {
            if (search.companies && search.companies.length > 0) {
              query["companies"] = search.companies;
            }
          } else if (key === "isFinishLater") {
            query["fl"] = search.isFinishLater;
          } else if (key === "yearMonth") {
            if (search.yearMonth.year) {
              query["year"] = search.yearMonth.year;
            }

            if (search.yearMonth.month) {
              query["month"] = search.yearMonth.month;
            }
          } else {
            if (search[key]) {
              if (typeof search[key] === "object") {
                if (search[key].sDate && search[key].sDate.trim()) {
                  query["sd"] = search[key].sDate.trim();
                }

                if (search[key].eDate && search[key].eDate.trim()) {
                  query["ed"] = search[key].eDate.trim();
                }
              } else {
                query[key] = search[key];
              }
            }
          }
        }
      }

      if (this.isJsonEmpty(search)) {
        // search (nav.value) 가 비어 있는 경우는 컴포너트가 생성되지 않은 경우 이므로, 컴포넌트의  query 데이터를 사용하여, search 데이터를 미리 넣어준다.
        if (this.stdSearchComponents && this.stdSearchComponents.length > 0) {
          const obj = {};
          for (const component of this.stdSearchComponents) {
            const _component_query = component.query;

            if (_component_query) {
              const keys = Object.keys(_component_query);
              for (const key of keys) {
                const data = _component_query[key];
                this.stringToObj(data, this.$route.query[key] || "", obj);
              }
            }
          }
          search = obj;
        }
      }

      // 기존 코드가 생성한 query 데이터를 제거한다
      // if (this.stdSearchComponents && this.stdSearchComponents.length > 0) {
      //   const _urlQuery = query;
      //   if (_urlQuery) {
      //     const _queryKeys = Object.keys(_urlQuery);
      //     for (const key of _queryKeys) {
      //       delete query[key];
      //     }
      //   }
      // }

      // 컴포넌트의 query 데이터를 사용하여, URL 쿼리에 데이터를 넣어준다.
      if (this.stdSearchComponents && this.stdSearchComponents.length > 0) {
        for (const component of this.stdSearchComponents) {
          const _queryObject = component.query;
          if (_queryObject) {
            const _queryKeys = Object.keys(_queryObject);
            for (const key of _queryKeys) {
              if (_queryKeys.length === 1) {
                query[key] = this.findJsonDataWithDottedPath(search, `${component.value}`);
              } else {
                query[key] = this.findJsonDataWithDottedPath(search, `${component.value}.${_queryObject[key]}`);
              }
            }
          }
        }
      }

      // 쿼리의 빈값을 제거한다
      const queryKeys = Object.keys(query);
      for (const key of queryKeys) {
        if (!query[key]) {
          delete query[key];
        }
      }

      if (this.moveList) {
        this.onMoveNormalPage("list", query);
      } else {
        this.$emit("list", query);
      }
    },

    isJsonEmpty(json) {
      return Object.keys(json).length === 0;
    },

    // dotted path를 통해서 json 데이터를 찾는다
    // ex> findJsonDataWithDottedPath({a: {b: {c: 1}}}, 'a.b.c') => 1
    findJsonDataWithDottedPath(json, path) {
      try {
        const _keys = Object.keys(json);
        if (_keys.length === 0) {
          return "";
        }
        const paths = path.split(".");
        let result = json;
        for (const p of paths) {
          result = result[p];
        }
        return result;
      } catch (err) {
        return "";
      }
    },
    stringToObj(path, value, obj) {
      const parts = path.split(".");
      const last = parts.pop();
      let part;
      while ((part = parts.shift())) {
        if (typeof obj[part] != "object") obj[part] = {};
        obj = obj[part]; // update "pointer"
      }
      obj[last] = value;
    },

    findKeyWithHead(sortKey) {
      if (singlePageList[`${this.boardId}_list`]) {
        const heads = singlePageList[`${this.boardId}_list`].getHead().map((e) => {
          return { [e.name]: e.key };
        });
        // 배열의 객체들을 하나의 객체로 합치기
        const types = Object.assign({}, ...heads);
        return `$${types[sortKey]}`;
      }
    },

    onMoveNormalPage(type, query) {
      const proj_id = `${this.projectId}`;
      const boardId = `${this.boardId}`;
      if (type === "list") {
        this.$router
          .push({ name: "list|tab", params: { pageType: "list", proj_id, boardId }, query })
          .catch((error) => {
            // NavigationDuplicated (중복된 네비게이션 시도)가 아닌 경우 에러를 표기
            if (error.name !== "NavigationDuplicated") {
              throw error;
            }
          })
          .finally(async () => {
            await this.loadList();
          });
      }
    },

    onClickMultiPrint() {
      this.$emit("clickMultiPrint");
    },

    onSelectAll() {
      if (this.parsedListForTable && this.parsedListForTable.length > 0) {
        this.$emit("selectAll", this.parsedListForTable);
      }
    },
    onClickPrint() {
      if (this.selectList && this.selectList.length > 0) {
        this.$emit("selectPrint");
      }
    },
    onClickSelectDownload() {
      this.$emit("clickSelectDownload");
    },

    onClickFileDownload(filename, path) {
      downloadFile(filename, path);
    },

    loadingDialog(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
  },
};
</script>

<style lang="scss" scoped>
.table-list-page {
  .page-head {
    min-width: 87px !important;
  }
}

.empty-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 60px 24px;
  margin: 20px auto;
  max-width: 400px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  color: #d1d5db;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.empty-description {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.body-child-table {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.body-child-table-row {
  display: flex;
  width: 100%;
  border-bottom: 1px solid #cdcdcd;

  &.is-last {
    border-bottom: 0;
  }
}

.body-child-table-cell {
  padding: 11px 4px;
  text-align: center;
  // flex 자식은 기본 min-width:auto 라서 공백 없는 긴 문자열(화학물질명 등)이 있으면 칸이 늘어나 표가 깨진다.
  // min-width:0 으로 줄어들 수 있게 하고, 공백이 없어도 칸 너비 안에서 줄바꿈되도록 한다.
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  & + .body-child-table-cell {
    border-left: 1px solid #cdcdcd;
  }
}
</style>
<style lang="scss">
.none-card {
  box-shadow: none;
  border-radius: 0px;
  display: inline-block;

  .vx-card__body {
    padding: 0px !important;
  }
}

span.up {
  color: #28c76f;
}
span.down {
  color: #ea5455;
}
.vs-table--tbody-table tr:nth-child(2n) {
  background-color: #f9f9f9;
}

.selectItem:nth-child(even) {
  background-color: #ea5455 !important;
  color: #fff !important;
}
.selectItem:nth-child(odd) {
  background-color: #ea5455 !important;
  color: #fff !important;
}
.table-list-page table {
  table-layout: fixed !important;
}
.table-list-page .vs-con-table table {
  width: auto;
  min-width: 100%;
}

.docs-file {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  .docs-files {
    &::after {
      content: ", ";
      color: rgb(126, 114, 242);
    }
    &:last-child::after {
      content: "";
    }
  }
}
</style>
